import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `adminPromos:` scope (F55), following the
 * `shares:`/`search:`/`sharing:` precedent (see
 * `apps/web/src/routes/shares/shares.i18n.ts`) — NOT a second i18n runtime
 * (E18/§13.3 ban); `packages/i18n/**` is outside this task's ownership.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/admin.json`.
 */
const en = {
  'page.title': 'Promo codes',
  'page.description': 'Create and manage promo codes — percent-off, fixed-off, and bonus-credits.',
  'action.create': '+ New code', // mockup-47: literal "+" glyph prefix, verbatim from mock
  'list.column.code': 'Code',
  'list.column.kind': 'Kind',
  'list.column.value': 'Value',
  'list.column.redemptions': 'Redeemed',
  'list.column.validity': 'Validity',
  'list.column.status': 'Status',
  'list.empty.title': 'No promo codes yet',
  'list.empty.description': 'Create your first promo code to offer a discount or bonus credits.',
  'list.error.title': "Couldn't load promo codes.",
  'list.noResults.title': 'No promo codes match “{query}”',
  'list.searchLabel': 'Search promo codes',
  'list.searchPlaceholder': 'Search by code…',
  'kind.percent_off': 'Percent off',
  'kind.fixed_off': 'Fixed amount off',
  'kind.bonus_credits': 'Bonus credits',
  'appliesTo.subscription': 'Subscription',
  'appliesTo.credit_pack': 'Credit pack',
  'appliesTo.any': 'Any',
  'status.active': 'Active',
  'status.inactive': 'Deactivated',
  'validity.noStart': 'No start date',
  'validity.noEnd': 'No end date',
  'action.edit': 'Edit',
  'action.deactivate': 'Deactivate',
  'action.viewRedemptions': 'View redemptions',
  'action.viewRedemptions.disabledReason': 'Per-redemption drill-down isn’t available yet (no admin.promo.redemptions.list op).',
  'form.title.create': 'New promo code',
  'form.title.edit': 'Edit promo code',
  'form.field.code': 'Code',
  'form.field.code.hint': 'Shown to users at checkout — case-insensitive.',
  'form.field.kind': 'Kind',
  'form.field.value.percent': 'Percent off',
  'form.field.value.fixed': 'Amount off',
  'form.field.value.credits': 'Bonus credits',
  'form.field.currency': 'Currency',
  'form.field.maxRedemptions': 'Max redemptions',
  'form.field.maxRedemptions.hint': 'Leave blank for unlimited.',
  'form.field.perUserLimit': 'Per-user limit',
  'form.field.validFrom': 'Valid from',
  'form.field.validUntil': 'Valid until',
  'form.field.appliesTo': 'Applies to',
  'form.action.save': 'Save',
  'form.action.cancel': 'Cancel',
  'toast.created.action': 'Created',
  'toast.updated.action': 'Updated',
  'toast.deactivated.action': 'Deactivated',
  'confirm.deactivate.action': 'Deactivate',
  'confirm.deactivate.description': 'Deactivating stops new redemptions — it can’t be hard-deleted once redeemed.',
} as const;

const ro = {
  'page.title': 'Coduri promoționale',
  'page.description': 'Creează și administrează coduri promoționale — reducere procentuală, fixă sau credite bonus.',
  'action.create': '+ Cod nou', // mockup-47
  'list.column.code': 'Cod',
  'list.column.kind': 'Tip',
  'list.column.value': 'Valoare',
  'list.column.redemptions': 'Utilizări',
  'list.column.validity': 'Valabilitate',
  'list.column.status': 'Stare',
  'list.empty.title': 'Niciun cod promoțional încă',
  'list.empty.description': 'Creează primul cod promoțional pentru o reducere sau credite bonus.',
  'list.error.title': 'Nu am putut încărca codurile promoționale.',
  'list.noResults.title': 'Niciun cod nu se potrivește cu „{query}”',
  'list.searchLabel': 'Caută coduri promoționale',
  'list.searchPlaceholder': 'Caută după cod…',
  'kind.percent_off': 'Reducere procentuală',
  'kind.fixed_off': 'Reducere fixă',
  'kind.bonus_credits': 'Credite bonus',
  'appliesTo.subscription': 'Abonament',
  'appliesTo.credit_pack': 'Pachet de credite',
  'appliesTo.any': 'Oricare',
  'status.active': 'Activ',
  'status.inactive': 'Dezactivat',
  'validity.noStart': 'Fără dată de start',
  'validity.noEnd': 'Fără dată de sfârșit',
  'action.edit': 'Editează',
  'action.deactivate': 'Dezactivează',
  'action.viewRedemptions': 'Vezi utilizările',
  'action.viewRedemptions.disabledReason': 'Detalierea utilizărilor nu este încă disponibilă (lipsește operația admin.promo.redemptions.list).',
  'form.title.create': 'Cod promoțional nou',
  'form.title.edit': 'Editează codul promoțional',
  'form.field.code': 'Cod',
  'form.field.code.hint': 'Afișat utilizatorilor la finalizarea comenzii — indiferent de majuscule.',
  'form.field.kind': 'Tip',
  'form.field.value.percent': 'Procent reducere',
  'form.field.value.fixed': 'Sumă reducere',
  'form.field.value.credits': 'Credite bonus',
  'form.field.currency': 'Monedă',
  'form.field.maxRedemptions': 'Utilizări maxime',
  'form.field.maxRedemptions.hint': 'Lasă gol pentru nelimitat.',
  'form.field.perUserLimit': 'Limită per utilizator',
  'form.field.validFrom': 'Valabil din',
  'form.field.validUntil': 'Valabil până la',
  'form.field.appliesTo': 'Se aplică la',
  'form.action.save': 'Salvează',
  'form.action.cancel': 'Anulează',
  'toast.created.action': 'Creat',
  'toast.updated.action': 'Actualizat',
  'toast.deactivated.action': 'Dezactivat',
  'confirm.deactivate.action': 'Dezactivează',
  'confirm.deactivate.description': 'Dezactivarea oprește utilizările noi — nu poate fi șters definitiv odată folosit.',
} as const;

export const ADMIN_PROMOS_CATALOG: Record<Locale, Record<AdminPromosCatalogKey, string>> = { en, ro };
export type AdminPromosCatalogKey = keyof typeof en;
